'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

export interface SavedListSummary { id: string; revision: string; name: string }
export interface SavedListDetail extends SavedListSummary { items: readonly unknown[] }
/** Structural subset of the storefront buying-list source; hosts supply the capability. */
export interface SavedListPickerSource {
  list(input?: { q?: string; page?: number; size?: number }): Promise<{ items: SavedListSummary[]; total?: number; page?: number; size?: number }>;
  detail(id: string): Promise<SavedListDetail>;
  create(input: { name: string; items: Array<{ productId: string; quantity: string }> }): Promise<SavedListDetail>;
  addItems(id: string, expectedRevision: string, items: Array<{ productId: string; quantity: string }>): Promise<SavedListDetail>;
  getScopeKey?(): string | undefined;
  setScopeKey?(scopeKey: string): void;
}
export function hasBuyingListsCapability(value: unknown): value is { buyingLists: SavedListPickerSource } {
  if (!value || typeof value !== 'object' || !('buyingLists' in value)) return false;
  const source = value.buyingLists;
  return Boolean(source && typeof source === 'object' && 'list' in source && typeof source.list === 'function'
    && 'detail' in source && typeof source.detail === 'function'
    && 'create' in source && typeof source.create === 'function'
    && 'addItems' in source && typeof source.addItems === 'function');
}

type Props = {
  source: SavedListPickerSource;
  scopeKey: string;
  product: { id: string; title: string };
  initialQuantity?: string;
};
const validId = (id: string) => /^[1-9][0-9]{0,18}$/.test(id) && BigInt(id) <= 9223372036854775807n;
const validQuantity = (quantity: string) => /^(?:0\.[0-9]{1,3}|[1-9][0-9]{0,3}(?:\.[0-9]{1,3})?)$/.test(quantity)
  && Number(quantity) > 0 && Number(quantity) <= 9999;
const statusOf = (cause: unknown): number | undefined => cause && typeof cause === 'object' && 'status' in cause
  && typeof cause.status === 'number' ? cause.status : undefined;

export function SavedListPicker({ source, scopeKey, product, initialQuantity = '1' }: Props) {
  const { t } = useTranslation('site-builder-blocks');
  const tr = (key: string, values?: Record<string, string | number>) => t(`commerce.savedListPicker.${key}`, values);
  const [open, setOpen] = useState(false);
  const [lists, setLists] = useState<SavedListSummary[]>([]);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [destination, setDestination] = useState('new');
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState(initialQuantity);
  const [state, setState] = useState<'idle' | 'loading' | 'pending' | 'signin' | 'conflict' | 'reviewed' | 'error' | 'success'>('idle');
  const [message, setMessage] = useState('');
  const [revision, setRevision] = useState<string>();
  const trigger = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const epoch = useRef(0);
  const listRequest = useRef(0);
  const pending = useRef(false);

  useEffect(() => {
    const current = ++epoch.current;
    source.setScopeKey?.(scopeKey);
    pending.current = false;
    listRequest.current++;
    setOpen(false); setLists([]); setQuery(''); setPage(0); setTotal(0); setDestination('new'); setName(''); setQuantity(initialQuantity);
    setState('idle'); setMessage(''); setRevision(undefined);
    return () => { if (epoch.current === current) epoch.current++; };
  }, [source, scopeKey, product.id, initialQuantity]);
  useEffect(() => {
    if (!open) return;
    dialog.current?.querySelector<HTMLSelectElement>('select')?.focus();
  }, [open]);
  function close() { if (pending.current) return; setOpen(false); trigger.current?.focus(); }
  async function loadLists(nextQuery = query, nextPage = page) {
    const started = epoch.current, request = ++listRequest.current;
    setState('loading'); setMessage('');
    try {
      const result = await source.list({ q: nextQuery.trim() || undefined, page: nextPage, size: 50 });
      if (started !== epoch.current || request !== listRequest.current) return;
      setLists(result.items.filter((item) => validId(item.id) && validId(item.revision)));
      setPage(nextPage); setTotal(result.total ?? result.items.length);
      setState('idle');
    } catch (cause) {
      if (started !== epoch.current || request !== listRequest.current) return;
      setState(statusOf(cause) === 401 ? 'signin' : 'error');
      setMessage(tr(statusOf(cause) === 401 ? 'signIn' : 'loadError'));
    }
  }
  function show() { setOpen(true); void loadLists(); }
  async function review() {
    if (destination === 'new') return;
    const started = epoch.current;
    setState('loading');
    try {
      const detail = await source.detail(destination);
      if (started !== epoch.current) return;
      if (!validId(detail.revision)) throw new Error('Invalid list revision');
      setRevision(detail.revision); setState('reviewed');
      setMessage(tr('review', { name: detail.name, revision: detail.revision }));
    } catch (cause) {
      if (started !== epoch.current) return;
      setState(statusOf(cause) === 401 ? 'signin' : 'error');
      setMessage(tr('reviewError'));
    }
  }
  async function submit() {
    if (pending.current) return;
    if (!validId(product.id) || !product.title.trim()) { setState('error'); setMessage(tr('invalidProduct')); return; }
    if (!validQuantity(quantity)) { setState('error'); setMessage(tr('invalidQuantity')); return; }
    if (destination === 'new' && (!name.trim() || name.trim().length > 200)) {
      setState('error'); setMessage(tr('invalidName')); return;
    }
    const started = epoch.current;
    pending.current = true; setState('pending'); setMessage('');
    try {
      const item = { productId: product.id, quantity };
      let result: SavedListDetail;
      if (destination === 'new') result = await source.create({ name: name.trim(), items: [item] });
      else {
        const current = revision ? { revision } : await source.detail(destination);
        if (started !== epoch.current) return;
        if (!validId(current.revision)) throw new Error('Invalid list revision');
        result = await source.addItems(destination, current.revision, [item]);
      }
      if (started !== epoch.current) return;
      setState('success');
      setMessage(tr('success', { quantity, product: product.title, name: result.name }));
      setRevision(undefined);
    } catch (cause) {
      if (started !== epoch.current) return;
      const status = statusOf(cause);
      setState(status === 401 ? 'signin' : status === 409 ? 'conflict' : 'error');
      setMessage(tr(status === 401 ? 'signIn' : status === 409 ? 'conflict' : 'error'));
      if (status === 409) setRevision(undefined);
    } finally { pending.current = false; }
  }
  return <>
    <button ref={trigger} type="button" onClick={show} style={buttonStyle}>{tr('addToList')}</button>
    {open && <div ref={dialog} role="dialog" aria-modal="true" aria-label={tr('dialog', { product: product.title })}
      onKeyDown={(event) => { if (event.key === 'Escape') { event.stopPropagation(); close(); } }} style={dialogStyle}>
      <h2 style={{ margin: '0 0 12px' }}>{tr('heading', { product: product.title })}</h2>
      <label style={fieldStyle}>{tr('findList')}<input type="search" value={query} onChange={(event) => { listRequest.current++; setQuery(event.target.value); setLists([]); setTotal(0); setPage(0); setState('idle'); }} maxLength={100} /></label>
      <button type="button" onClick={() => void loadLists(query, 0)} disabled={state === 'pending'}>{tr('searchLists')}</button>
      <label style={fieldStyle}>{tr('destination')}
        <select value={destination} onChange={(event) => { setDestination(event.target.value); setRevision(undefined); setState('idle'); setMessage(''); }} disabled={pending.current}>
          <option value="new">{tr('newList')}</option>
          {lists.map((list) => <option key={list.id} value={list.id}>{list.name}</option>)}
        </select>
      </label>
      {total > 50 && <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button type="button" onClick={() => void loadLists(query, page - 1)} disabled={page === 0 || state === 'loading'}>{tr('previous')}</button>
        <span>{tr('page', { page: page + 1, pages: Math.ceil(total / 50) })}</span>
        <button type="button" onClick={() => void loadLists(query, page + 1)} disabled={(page + 1) * 50 >= total || state === 'loading'}>{tr('next')}</button>
      </div>}
      {destination === 'new' && <label style={fieldStyle}>{tr('newName')}<input value={name} maxLength={200} onChange={(event) => setName(event.target.value)} /></label>}
      <label style={fieldStyle}>{tr('quantity')}<input inputMode="decimal" value={quantity} onChange={(event) => setQuantity(event.target.value)} /></label>
      {state === 'loading' && <p role="status">{tr('loading')}</p>}
      {message && <p role={state === 'success' || state === 'reviewed' ? 'status' : 'alert'}>{message}</p>}
      {state === 'signin' && <p>{tr('signinHelp')}</p>}
      {state === 'conflict' && <button type="button" onClick={() => void review()}>{tr('reviewLatest')}</button>}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        {state !== 'success' && <button type="button" onClick={() => void submit()} disabled={state === 'pending' || state === 'loading' || state === 'conflict'}>{tr(state === 'pending' ? 'adding' : state === 'signin' || state === 'error' || state === 'reviewed' ? 'retry' : 'addProduct')}</button>}
        <button type="button" onClick={close} disabled={state === 'pending'}>{tr(state === 'success' ? 'done' : 'cancel')}</button>
      </div>
    </div>}
  </>;
}

const dialogStyle: CSSProperties = { position: 'fixed', zIndex: 1000, inset: 'auto 1rem 1rem auto', width: 'min(420px, calc(100vw - 2rem))', maxHeight: 'calc(100vh - 2rem)', overflow: 'auto', padding: 20, border: '1px solid var(--color-border-default, #ccc)', borderRadius: 'var(--radius-card, 12px)', background: 'var(--color-surface-card, #fff)', color: 'var(--color-text-primary, #18181b)', boxShadow: 'var(--shadow-lg, 0 16px 36px rgba(0,0,0,.13))' };
const fieldStyle: CSSProperties = { display: 'grid', gap: 6, marginTop: 12 };
const buttonStyle: CSSProperties = { border: '1px solid var(--color-border-default, #ccc)', borderRadius: 'var(--radius-sm, 6px)', background: 'var(--color-surface-card, #fff)', color: 'var(--color-text-primary, #18181b)', padding: '8px 12px', cursor: 'pointer' };
